import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapPin, Search, Bell, ShoppingCart, ChevronDown, X, Check, Plus, AlertTriangle } from "lucide-react";
import EmptyState from "./EmptyState";
import api, { friendlyMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getTenantSlug } from "../lib/tenant";
import { useCart } from "../lib/cart";
import { formatINR, formatRelative } from "../lib/utils.fmt";
import ProductImage from "./ProductImage";
import { toast } from "sonner";
import { ADDRESS_CHANGED_EVENT, notifyAddressChanged, onAddressChanged } from "../lib/addressBus";

/**
 * Persistent customer header for Home / Shop / Product Detail.
 * Layout: [Address chip ▾]    [🔍] [🔔] [🛒]
 *
 * - Address chip → dropdown listing the saved addresses; picking one makes
 *   it the delivery address in place (no navigation). "Manage addresses"
 *   at the bottom still opens the full add/edit/delete screen.
 * - Search → modal that fuzzy-filters products via /api/customer/home
 *   (the public storefront endpoint for a signed-out visitor)
 * - Bell → dropdown reading /api/customer/notifications
 * - Cart → /app/checkout (with badge)
 */
export default function CustomerHeader({ primary = "#3A86FF" }) {
  const { user } = useAuth();
  const cart = useCart();
  const nav = useNavigate();
  const location = useLocation();
  const [defaultAddr, setDefaultAddr] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState("");
  const addrRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const bellRef = useRef(null);

  // Pull addresses + products once on mount (only when signed in).
  const loadAddresses = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/api/customer/addresses");
      const list = data.addresses || [];
      setAddresses(list);
      setDefaultAddr(list.find((a) => a.is_default) || list[0] || null);
    } catch { /* chip falls back to "Add an address" */ }
  }, [user]);

  useEffect(() => {
    loadAddresses();
    // Products for the search sheet. A signed-out visitor reads the public
    // storefront: the signed-in endpoint answers them 401, which the app
    // treats as "go to login" — so this one call used to throw every
    // logged-out visitor off the store and onto the login screen.
    const source = user ? "/api/customer/home" : `/api/public/tenant/${getTenantSlug()}/home`;
    api.get(source).then(({ data }) => {
      setProducts(data.products || []);
    }).catch(() => {});
  }, [user, loadAddresses]);

  // Someone else changed the address list (ManageAddresses add/edit/delete,
  // or the AddressDialog on another screen) — keep the chip honest.
  useEffect(() => {
    return onAddressChanged(() => loadAddresses());
  }, [loadAddresses]);

  // Close the address dropdown on outside click (same pattern as the bell).
  useEffect(() => {
    if (!addrOpen) return;
    const onClick = (e) => {
      if (addrRef.current && !addrRef.current.contains(e.target)) setAddrOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [addrOpen]);

  const selectAddress = async (a) => {
    if (a.is_default) { setAddrOpen(false); return; }
    setSwitchingId(a.id);
    // Optimistic: the chip updates the instant the customer taps, rather than
    // after a round-trip. Rolled back below if the server rejects.
    const previous = { addresses, defaultAddr };
    setAddresses((list) => list.map((x) => ({ ...x, is_default: x.id === a.id })));
    setDefaultAddr(a);
    try {
      await api.post(`/api/customer/addresses/${a.id}/default`);
      setAddrOpen(false);
      // Home (and any other screen reading addresses) re-reads so its
      // serviceability banner reflects the address just picked.
      notifyAddressChanged();
      // Re-fetch so `serviceable` / `allowed_slots` come from the server
      // rather than being inferred from the stale row we optimistically set.
      loadAddresses();
    } catch (e) {
      setAddresses(previous.addresses);
      setDefaultAddr(previous.defaultAddr);
      toast.error(friendlyMessage(e));
    } finally { setSwitchingId(""); }
  };

  // Close bell dropdown on outside click.
  useEffect(() => {
    if (!bellOpen) return;
    const onClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [bellOpen]);

  const openBell = async () => {
    setBellOpen((o) => !o);
    if (!user) return;
    try {
      const { data } = await api.get("/api/customer/notifications");
      setNotifications(data.notifications || []);
    } catch { setNotifications([]); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return products.slice(0, 12);
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.brand_name || "").toLowerCase().includes(needle) ||
      (p.category_name || "").toLowerCase().includes(needle),
    ).slice(0, 20);
  }, [q, products]);

  return (
    <>
      {/* Native AppBar: sticky top-0 with safe-area-inset-top padding
          INSIDE the header, so its solid background always covers the
          status-bar area at every scroll position. No separate backdrop
          div required — matches Material 3 / iOS AppBar behaviour. */}
      <header className="sticky top-0 z-30 bg-brand-cream pt-safe" data-testid="customer-header">
        <div className="px-4 sm:px-5 py-3 flex items-center gap-2">
          {/* Address chip — carries where we were (Home / Shop / Product
              Detail, whichever page renders this header) as router state so
              ManageAddresses' back button can return here instead of always
              falling back to Profile. */}
          <div className="min-w-0 flex-1 relative" ref={addrRef}>
            <button
              onClick={() => {
                // Signed out, or nothing saved yet — there's no list to pick
                // from, so go straight to the screen that can create one.
                if (!user || addresses.length === 0) {
                  nav("/app/addresses", { state: { from: location.pathname + location.search } });
                  return;
                }
                setAddrOpen((o) => !o);
              }}
              className="w-full min-w-0 inline-flex items-center gap-2 h-11 px-3 rounded-2xl bg-white border border-neutral-200 active:scale-[0.99]"
              data-testid="header-address-chip"
              aria-haspopup="listbox"
              aria-expanded={addrOpen}>
              <MapPin className="w-4 h-4 shrink-0" style={{ color: primary }}/>
              <div className="min-w-0 text-left">
                <div className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 leading-none">Delivery to</div>
                <div className="text-xs font-extrabold text-neutral-800 truncate leading-tight mt-0.5">
                  {defaultAddr ? `${defaultAddr.label} · ${defaultAddr.city || ""}` : (user ? "Add an address" : "Sign in")}
                </div>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-neutral-400 shrink-0 ml-auto transition-transform ${addrOpen ? "rotate-180" : ""}`}/>
            </button>

            {addrOpen && (
              <div className="absolute z-40 mt-1.5 w-[min(22rem,calc(100vw-2rem))] bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden"
                   role="listbox" data-testid="header-address-list">
                <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                  Deliver to
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {addresses.map((a) => {
                    const active = a.is_default;
                    // `serviceable` is hydrated server-side on every address
                    // (delivery_validation.check_address_serviceable), so the
                    // customer can see which ones can actually be delivered to
                    // BEFORE switching, instead of discovering it after.
                    const unserviceable = a.serviceable === false;
                    return (
                      <button key={a.id} onClick={() => selectAddress(a)}
                              disabled={!!switchingId}
                              className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-neutral-100 last:border-b-0 disabled:opacity-60 ${active ? "bg-brand-sky/5" : "hover:bg-neutral-50"}`}
                              role="option" aria-selected={active}
                              data-testid={`header-address-option-${a.id}`}>
                        <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-brand-sky" : "text-neutral-400"}`}/>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-sm font-bold ${active ? "text-brand-sky" : "text-neutral-800"}`}>{a.label || "Address"}</span>
                            {unserviceable && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-clay/10 text-brand-clay text-[9px] font-extrabold uppercase tracking-wider"
                                    data-testid={`header-address-unserviceable-${a.id}`}>
                                <AlertTriangle className="w-2.5 h-2.5"/> Not serviceable
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-neutral-500 break-words mt-0.5">
                            {[a.line1, a.city, a.pincode].filter(Boolean).join(", ")}
                          </span>
                        </span>
                        {active && <Check className="w-4 h-4 text-brand-sky shrink-0 mt-0.5"/>}
                      </button>
                    );
                  })}
                </div>
                {/* Add/edit/delete still live on the dedicated screen — this
                    dropdown only switches between what's already saved. */}
                <Link to="/app/addresses" state={{ from: location.pathname + location.search }}
                      onClick={() => setAddrOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 border-t border-neutral-100 text-sm font-bold text-brand-sky"
                      data-testid="header-manage-addresses">
                  <Plus className="w-4 h-4"/> Add or manage addresses
                </Link>
              </div>
            )}
          </div>

          {/* Action icons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => setSearchOpen(true)} className="w-11 h-11 rounded-2xl bg-white border border-neutral-200 inline-flex items-center justify-center" data-testid="header-search-btn" aria-label="Search">
              <Search className="w-5 h-5 text-neutral-700"/>
            </button>
            <div className="relative" ref={bellRef}>
              <button onClick={openBell} className="w-11 h-11 rounded-2xl bg-white border border-neutral-200 inline-flex items-center justify-center relative" data-testid="header-bell-btn" aria-label="Notifications">
                <Bell className="w-5 h-5 text-neutral-700"/>
                {notifications.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-clay text-white text-[9px] font-extrabold inline-flex items-center justify-center" data-testid="bell-badge">
                    {notifications.length > 9 ? "9+" : notifications.length}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-12 w-[300px] max-w-[calc(100vw-32px)] bg-white border border-neutral-200 rounded-2xl shadow-xl z-40 overflow-hidden" data-testid="bell-dropdown">
                  <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
                    <div className="font-extrabold text-sm">Notifications</div>
                    <button onClick={() => setBellOpen(false)} className="w-7 h-7 rounded-full inline-flex items-center justify-center text-neutral-500" aria-label="Close" data-testid="bell-close">
                      <X className="w-4 h-4"/>
                    </button>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <EmptyState icon={Bell} size="sm" title="No notifications yet"
                                  description="We'll ping you when there's an update." testId="bell-empty" />
                    ) : (
                      notifications.map((n) => (
                        <Link key={n.id} to={n.deep_link || "/app/home"}
                              onClick={() => setBellOpen(false)}
                              className="block px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50"
                              data-testid={`notif-${n.id}`}>
                          <div className="font-bold text-sm text-neutral-800 leading-tight">{n.title || "Update"}</div>
                          {n.body && <div className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</div>}
                          <div className="text-[10px] text-neutral-400 mt-1">{formatRelative ? formatRelative(n.created_at) : n.created_at}</div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => nav("/app/checkout")} className="w-11 h-11 rounded-2xl bg-white border border-neutral-200 inline-flex items-center justify-center relative" data-testid="header-cart-btn" aria-label="Cart">
              <ShoppingCart className="w-5 h-5 text-neutral-700"/>
              {cart?.count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-leaf text-white text-[9px] font-extrabold inline-flex items-center justify-center" data-testid="cart-badge">
                  {cart.count > 9 ? "9+" : cart.count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-20 px-4" onClick={() => setSearchOpen(false)} data-testid="search-modal">
          <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
              <Search className="w-5 h-5 text-neutral-400 shrink-0"/>
              <input autoFocus type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search products, brands, categories…"
                     className="flex-1 h-10 text-base outline-none bg-transparent"
                     data-testid="search-input"/>
              <button onClick={() => setSearchOpen(false)} className="w-8 h-8 rounded-full inline-flex items-center justify-center" aria-label="Close" data-testid="search-close">
                <X className="w-5 h-5 text-neutral-500"/>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {filtered.length === 0 ? (
                <EmptyState icon={Search} size="sm"
                            title={q ? "No products match your search" : "Type to search products"}
                            testId="search-empty" />
              ) : (
                filtered.map((p) => (
                  <Link key={p.id} to={`/app/products/${p.id}`}
                        onClick={() => setSearchOpen(false)}
                        className="block px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 flex items-center gap-3"
                        data-testid={`search-result-${p.id}`}>
                    <ProductImage src={p.image_url} name={p.name} primary={primary}
                                  className="w-11 h-11 rounded-xl overflow-hidden shrink-0"
                                  badgeClassName="w-7 h-7 text-[9px]"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{p.brand_name || p.category_name}</div>
                    </div>
                    <div className="font-mono font-extrabold text-sm shrink-0">{formatINR(p.price)}</div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
